import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useNegocio } from './AliadoDashboardLayout';
import { Layers, QrCode, ExternalLink, Smartphone, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

export default function AliadoPasillosPage() {
    const negocio = useNegocio();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedCat, setSelectedCat] = useState('all');

    useEffect(() => {
        Promise.all([
            api.get('/me/oknegocio/products'),
            api.get('/me/oknegocio/product-categories'),
        ]).then(([p, c]) => {
            setProducts(p.data);
            setCategories(c.data);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const catalogUrl = negocio ? `${window.location.origin}/aliado/${negocio.slug}#catalogo` : '';

    const copyLink = () => {
        navigator.clipboard.writeText(catalogUrl);
        toast.success('Enlace copiado');
    };

    const filtered = products.filter(p => {
        const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.description || '').toLowerCase().includes(search.toLowerCase());
        const matchCat = selectedCat === 'all' || p.category_id === selectedCat;
        return matchSearch && matchCat && p.available;
    });

    const catName = (id) => categories.find(c => c.id === id)?.name || '—';

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-xl font-bold flex items-center gap-2"><Layers className="h-5 w-5 text-orange-600" /> OkPasillos</h1>
                <p className="text-sm text-muted-foreground">Catálogo digital interactivo con código QR para tus clientes.</p>
            </div>

            {/* QR + link panel */}
            <div className="rounded-2xl border bg-card p-5 flex flex-col sm:flex-row items-center gap-5">
                <div className="h-28 w-28 rounded-2xl bg-muted flex items-center justify-center shrink-0">
                    <QrCode className="h-14 w-14 text-muted-foreground" />
                </div>
                <div className="space-y-3 text-center sm:text-left">
                    <div>
                        <div className="font-semibold text-sm">Tu catálogo digital</div>
                        <div className="text-xs text-muted-foreground mt-0.5 break-all">{catalogUrl}</div>
                    </div>
                    <div className="flex gap-2 flex-wrap justify-center sm:justify-start">
                        <Button size="sm" onClick={copyLink} variant="outline" className="h-8 text-xs">
                            Copiar enlace
                        </Button>
                        <Button size="sm" onClick={() => window.open(catalogUrl, '_blank')} variant="outline" className="h-8 text-xs">
                            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver catálogo
                        </Button>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Smartphone className="h-3 w-3" /> Coloca este QR en tu tienda para que los clientes vean tu catálogo
                    </div>
                </div>
            </div>

            {/* Catalog preview */}
            {loading ? (
                <div className="text-sm text-muted-foreground text-center py-6">Cargando catálogo…</div>
            ) : products.length === 0 ? (
                <div className="rounded-2xl border bg-card p-8 text-center">
                    <Layers className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <div className="text-sm text-muted-foreground">Tu catálogo está vacío. <a href="/aliado/dashboard/products" className="text-orange-600 hover:underline">Agrega productos</a> y aparecerán aquí.</div>
                </div>
            ) : (
                <>
                    {/* Filters */}
                    <div className="flex gap-2 flex-wrap">
                        <div className="relative flex-1 min-w-36">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                            <Input className="pl-8 h-8 text-xs" placeholder="Buscar en catálogo…" value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                        <div className="flex gap-1 flex-wrap">
                            <button onClick={() => setSelectedCat('all')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedCat === 'all' ? 'bg-orange-600 text-white' : 'border hover:bg-muted'}`}>
                                Todos
                            </button>
                            {categories.map(c => (
                                <button key={c.id} onClick={() => setSelectedCat(c.id)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedCat === c.id ? 'bg-orange-600 text-white' : 'border hover:bg-muted'}`}>
                                    {c.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-6">No se encontraron productos.</div>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {filtered.map(p => (
                                <div key={p.id} className="rounded-2xl border bg-card overflow-hidden hover:shadow-sm transition-shadow">
                                    {p.image_url
                                        ? <img src={p.image_url} alt={p.name} className="h-36 w-full object-cover" />
                                        : <div className="h-36 bg-muted flex items-center justify-center text-4xl">📦</div>
                                    }
                                    <div className="p-3">
                                        <div className="font-medium text-sm">{p.name}</div>
                                        {p.category_id && categories.length > 0 && (
                                            <div className="text-[10px] text-muted-foreground">{catName(p.category_id)}</div>
                                        )}
                                        {p.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</div>}
                                        <div className="mt-2 flex items-center justify-between">
                                            <div>
                                                <span className="text-sm font-bold text-orange-700">{fmt(p.price)}</span>
                                                {p.compare_price > 0 && <span className="text-xs line-through text-muted-foreground ml-1">{fmt(p.compare_price)}</span>}
                                            </div>
                                            {p.stock > 0 && <span className="text-[10px] text-muted-foreground">{p.stock} en stock</span>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
